<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
            <Loader :visible="isLoading" :text="loadingText" />
            <ConfirmationModal :show="modal.show" @close="modal.show = false">
                <template #title>
                    {{ modal.type === 'success' ? 'Operazione Completata' : 'Errore' }}
                </template>

                <template #content>
                    {{ modal.message }}
                </template>

                <template #footer>
                    <PrimaryButton @click="modal.show = false">
                        {{ $t('core.ok') }}
                    </PrimaryButton>
                </template>
            </ConfirmationModal>

            <div class="mt-6 space-y-6">
                <div class="border rounded-lg bg-gray-50 p-5">
                    <h3 class="text-md font-bold mb-4">Dati Documento</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium mb-1">Fornitore</label>
                            <v-select
                                :options="suppliers"
                                label="CompanyName"
                                v-model="selectedSupplier"
                                :filterable="true"
                                placeholder="Cerca un fornitore..."
                                :disabled="addedItems.length > 0" 
                            >
                                <template #option="{ CompanyName }">
                                    <span>{{ CompanyName }}</span>
                                </template>
                                <template #selected-option="{ CompanyName }">
                                    <span>{{ CompanyName }}</span>
                                </template>
                                <template #no-options>
                                    Nessun fornitore trovato.
                                </template>
                            </v-select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Causale</label>
                            <v-select
                                :options="inventoryReasons"
                                label="Reason"
                                v-model="selectedInvReason"
                                :filterable="true"
                                :disabled="!selectedSupplier || addedItems.length > 0"
                                placeholder="Cerca una causale..."
                            >
                                <template #option="{ Reason, Description }">
                                    <span>{{ Reason }}</span>
                                    <span v-if="Description" class="text-gray-500 ml-2">- {{ Description }}</span>
                                </template>
                                <template #selected-option="{ Reason, Description }">
                                    <span>{{ Reason }}</span>
                                    <span v-if="Description" class="text-gray-500 ml-2">- {{ Description }}</span>
                                </template>
                                <template #no-options>
                                    Nessuna causale trovata.
                                </template>
                            </v-select>

                        </div>
                    </div>
                </div>

                <div v-if="selectedSupplier && selectedInvReason" class="border rounded-lg bg-gray-50 p-5">
                    <h3 class="text-md font-bold mb-4">Aggiungi Articoli</h3>
                    
                    <div :class="[usaValoreUnitario ? 'grid grid-cols-1 md:grid-cols-4 gap-4 items-end' : 'grid grid-cols-1 md:grid-cols-3 gap-4 items-end']">
                        <div class="md:col-span-2">
                            <label class="block text-sm font-medium mb-1">Item</label>
                            <v-select
                                :options="items"
                                label="Item"
                                v-model="selectedItem"
                                :filterable="false"
                                @search="handleItemSearch"
                                placeholder="Cerca un item..."
                            >
                                <template #option="{ Item, Description }">
                                    <span>{{ Item }}</span>
                                    <span v-if="Description" class="text-gray-500 ml-2">- {{ Description }}</span>
                                </template>
                                <template #selected-option="{ Item, Description }">
                                    <span>{{ Item }}</span>
                                    <span v-if="Description" class="text-gray-500 ml-2">- {{ Description }}</span>
                                </template>
                                <template #no-options>
                                    {{ itemSearchLoading ? 'Ricerca in corso...' : itemSearchQuery.length < 3 ? 'Digita almeno 3 caratteri.' : 'Nessun item trovato.' }}
                                </template>
                            </v-select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Quantità</label>
                            <div class="relative">
                                <input
                                    type="number"
                                    v-model="quantity"
                                    placeholder="Qtà"
                                    class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'pr-16': selectedItem }"
                                />
                                <span v-if="selectedItem" class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">
                                    {{ selectedItem.BaseUoM }}
                                </span>
                            </div>
                        </div>
                        <div v-if="usaValoreUnitario">
                            <label class="block text-sm font-medium mb-1">Valore Unitario</label>
                            <div class="relative">
                                <input
                                    type="number"
                                    v-model="unitValue"
                                    placeholder="Valore Unitario"
                                    class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'pr-16': selectedItem }"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="handleLots" class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end mt-4">
                        <div v-if="usaMatricola">
                            <label class="block text-sm font-medium ml-2 mb-1">Matricola</label>
                            <input
                                type="text"
                                v-model="matricolaValue"
                                placeholder="Matricola"
                                class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div v-else>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Lotto</label>
                            <div class="flex gap-2">
                                <input
                                    type="text"
                                    v-model="lotValue"
                                    placeholder="Codice lotto"
                                    class="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    type="button"
                                    @click="generateLot(selectedItem)"
                                    class="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                                    title="Genera lotto"
                                >
                                    <i class="fa fa-magic mr-1"></i> Genera Lotto
                                </button>
                            </div>
                        </div>
                        
                        <div></div>
                        <div class="flex justify-end mt-4">
                            <PrimaryButton type="button" :disabled="!canAdd" @click="addItem">
                                <i class="fa fa-plus"></i> Aggiungi Articolo
                            </PrimaryButton>
                        </div>
                        
                    </div>
                    <div v-else class="flex justify-end mt-4">
                        <PrimaryButton type="button" :disabled="!canAdd" @click="addItem">
                            <i class="fa fa-plus"></i> Aggiungi Articolo
                        </PrimaryButton>
                    </div>
                           
                </div>    

                <div v-if="addedItems.length" class="border rounded-lg bg-white p-5">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-md font-bold">Articoli da caricare ({{ addedItems.length }})</h3>
                        <div class="flex items-center gap-2">
                            <DangerButton 
                                v-if="addedItems.length > 0" 
                                type="button" 
                                @click="resetForm"
                            >
                                <i class="fa fa-undo mr-2"></i> Reset
                            </DangerButton>
                            
                            <PrimaryButton type="button" @click="loadComponents">
                                <i class="fa fa-check mr-2"></i> Carica componenti
                            </PrimaryButton>
                        </div>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrizione</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Causale</th>
                                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantità</th>
                                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unità di Misura</th>
                                    <th v-if="usaValoreUnitario" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valore Unitario</th>
                                    <th v-if="handleLots" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lotto</th>
                                    <th v-if="usaMatricola" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matricola</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codice IVA</th>
                                    <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20"></th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="(item, index) in addedItems" :key="index" class="hover:bg-gray-50">
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.Item }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ item.Description }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ item.Reason }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-mono">{{ item.Quantity }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-mono">{{ item.UoM }}</td>
                                    <td v-if="usaValoreUnitario" class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-mono">{{ item.UnitValue }}</td>
                                    <td v-if="handleLots" class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ item.Lot }}</td>
                                    <td v-if="usaMatricola" class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ item.Matricola }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ item.TaxCode }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-center">
                                        <button type="button" @click="removeItem(index)" class="text-red-500 hover:text-red-700">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>



<script>
import BreadCrumbs from "@/Components/BreadCrumbs.vue";
import ConfirmationModal from "@/Components/ConfirmationModal.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import DangerButton from "@/Components/DangerButton.vue";
import ToastNotification from "@/Components/ToastNotification.vue";
import Loader from "@/Components/Loader.vue";
import PageNormalTable from "@/Components/PageNormalTable.vue";
import { useLoader } from "@/Composables/useLoader.js";
import axios from "axios";

export default {
    name: "CaricoComponenti",

    setup() {
        const { isLoading, loadingText, showLoader, hideLoader } = useLoader();
        return { isLoading, loadingText, showLoader, hideLoader };
    },

    components: {
        BreadCrumbs,
        ConfirmationModal,
        PrimaryButton,
        DangerButton,
        ToastNotification,
        Loader,
        PageNormalTable,
    },

    props: {
        inventoryReasons: Object,
        suppliers: Object,
        defaultReason: String,
        handleLots: Boolean,
        lotConfig: Object,
        usaMatricola: Boolean,
        usaValoreUnitario: Boolean,

    },

    data() {
        return {
            selectedItem: null,
            selectedInvReason: null,
            selectedSupplier: null,
            items: [],
            itemSearchQuery: '',
            itemSearchLoading: false,
            itemSearchTimeout: null,
            itemSearchRequestId: 0,
            quantity: null,
            unitValue: null,
            addedItems: [],
            modal: { 
                show: false, 
                type: '', 
                message: '' 
            },
            lotValue: '',
            matricolaValue: '',
        };
    },

    computed: {
        canAdd() {
            if (this.usaValoreUnitario) {
                return this.selectedSupplier && this.selectedItem && this.selectedInvReason && this.quantity && this.unitValue > 0;
            } else {
                return this.selectedSupplier && this.selectedItem && this.selectedInvReason && this.quantity;
            }
        },
        navigation() {
            return [
                { name: "Dashboard", href: route("dashboard") },
                {
                    name: this.$t("modules.gestione-magazzino"),
                    href: route("modules.home", "gestione-magazzino"),
                },
                {
                    name: this.$t("modules.ricezione-merce-senza-ordine"),
                    href: "#", 
                },
            ];
        },
        
    },

    mounted() {
        if (this.defaultReason) {
            this.selectedInvReason = this.inventoryReasons.find(
                r => r.Reason === this.defaultReason
            ) || null;
        }
    },

    methods: {
        addItem() {
            this.addedItems.push({
                Item: this.selectedItem.Item,
                Description: this.selectedItem.Description,
                Reason: this.selectedInvReason.Reason,
                Quantity: this.quantity,
                UoM: this.selectedItem.BaseUoM,
                Lot: this.lotValue, 
                Matricola: this.matricolaValue,
                TaxCode: this.selectedItem.TaxCode,
                UnitValue: this.unitValue || 0,
            });
            this.selectedItem = null;
            this.quantity = null;
            this.lotValue = '';
            this.matricolaValue = '';
            this.unitValue = '';
        },  
        handleItemSearch(search, loading) {
            this.itemSearchQuery = search.trim();
            const requestId = ++this.itemSearchRequestId;

            clearTimeout(this.itemSearchTimeout);

            if (this.itemSearchQuery.length < 3) {
                this.items = [];
                this.itemSearchLoading = false;
                loading(false);
                return;
            }

            this.items = [];
            this.itemSearchLoading = true;
            loading(true);

            this.itemSearchTimeout = setTimeout(() => {
                axios.post(route('modules.gm.cerca-articoli-carico'), { search: this.itemSearchQuery })
                    .then(response => {
                        if (requestId === this.itemSearchRequestId) {
                            this.items = response.data.items || [];
                        }
                    })
                    .catch(error => {
                        if (requestId === this.itemSearchRequestId) {
                            this.items = [];
                            console.error(error);
                        }
                    })
                    .finally(() => {
                        if (requestId === this.itemSearchRequestId) {
                            this.itemSearchLoading = false;
                            loading(false);
                        }
                    });
            }, 300);
        },
        fetchItemsBySupplier(supplier) {
            this.showLoader('Caricamento items...');
            axios.post(route('modules.gm.getitemsbysupplier'), { supplier: supplier.CustSupp })
                .then(response => {
                    this.supplierItems = response.data;
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.hideLoader();
                });
        },
        removeItem(index) {
            this.addedItems.splice(index, 1);
        },
        resetForm() {
            this.addedItems = [];
            this.selectedSupplier = null;
            this.selectedInvReason = null;
            this.selectedItem = null;
            this.quantity = null;
            this.lotValue = '';
            this.matricolaValue = '';
        },

        loadComponents() {
            let payload = {
                supplier: this.selectedSupplier.CustSupp,
                reason: this.selectedInvReason.Reason,
                items: this.addedItems.map(item => ({
                    Item: item.Item,
                    Quantity: item.Quantity,
                    Lot: item.Lot,
                    Matricola: item.Matricola,
                    UoM: item.UoM,
                    Description: item.Description,
                    UnitValue: item.UnitValue || 0,
                    TaxCode: item.TaxCode,
                    PurchaseType: item.PurchaseType,
                })),
            };

            
            this.showLoader('Caricamento componenti...');
            axios.post(route('modules.gm.carico'), payload)
                .then(response => {
                    this.hideLoader();
                    if (response.data.success) {
                        this.modal = { 
                            show: true, 
                            type: 'success', 
                            message: response.data.message 
                        };

                        this.resetForm();
                    } else {
                        this.modal = { 
                            show: true, 
                            type: 'error', 
                            message: response.data.message 
                        };

                    }
                })
                .catch(error => {
                    this.modal = {
                        show: true,
                        type: 'error',
                        message: error.response?.data?.message || 'Errore di connessione',
                    };
                })
                .finally(() => {
                    this.hideLoader();
                });
        },
        generateLot(item) {
            if (!this.lotConfig) {
                this.modal = { show: true, type: 'error', message: 'Configurazione lotto non disponibile' };
                return;
            }
            
            const searchPattern = this.buildSearchPattern();

            const usedLots = (this.addedItems || [])
                .filter(row => row.Lot && row.Lot.trim() !== '')
                .map(row => row.Lot.trim());

            axios.post(route('modules.gm.getNextLot'), { 
                prefix: searchPattern.prefix,
                suffix: searchPattern.suffix,
                item: item?.Item,
                usedLots
            }).then((response) => {
                if (response.data.success) {
                    const generatedLot = response.data.nextLot || response.data.lastLot;
                    this.lotValue = generatedLot;
                    this.modal = { show: true, type: 'success', message: this.$t('gm.lot_generated') + generatedLot };
                } else {
                    this.modal = { show: true, type: 'error', message: this.$t('core.error') };
                }
            }).catch((error) => {
                this.modal = { show: true, type: 'error', message: this.$t('core.error') + ': ' + (error.response?.data?.message || error.message) };
            });
        },
        buildSearchPattern() {
            const config = this.lotConfig;
            let searchPrefix = '';
            
            if (config.prefix && config.prefix.trim() !== '') {
                searchPrefix += config.prefix.trim();
            }

            if (config.year_format === '2-digit') {
                searchPrefix += new Date().getFullYear().toString().slice(-2);
            } else if (config.year_format === '4-digit') {
                searchPrefix += new Date().getFullYear().toString();
            }
            
            if (searchPrefix.length > 0 && config.separator && config.separator !== 'none') {
                searchPrefix += config.separator;
            }
            
            const searchSuffix = (config.suffix && config.suffix.trim() !== '') ? config.suffix.trim() : '';
            
            return {
                prefix: searchPrefix,
                suffix: searchSuffix
            };
        },
        extractProgressive(lot, prefix, suffix) {
            let progressive = lot;
            
            if (prefix && progressive.startsWith(prefix)) {
                progressive = progressive.substring(prefix.length);
            }
            
            if (suffix && progressive.endsWith(suffix)) {
                progressive = progressive.substring(0, progressive.length - suffix.length);
            }
            
            return parseInt(progressive, 10) || 0;
        },
        findFirstAvailableProgressive(startProgressive, prefix, suffix, excludeItem) {
            let currentProgressive = startProgressive;
            
            while (this.isProgressiveUsedInRows(currentProgressive, prefix, suffix, excludeItem)) {
                currentProgressive++;
            }
            
            return currentProgressive;
        },
        isProgressiveUsedInRows(progressive, prefix, suffix, excludeItem) {
            return this.addedItems.some(row => {
                if (row === excludeItem) return false;
                if (row.Lot && row.Lot.trim() !== '') {
                    const rowLot = row.Lot.trim();
                    const rowProgressive = this.extractProgressive(rowLot, prefix, suffix);
                    const matchesPrefix = !prefix || rowLot.startsWith(prefix);
                    const matchesSuffix = !suffix || rowLot.endsWith(suffix);
                    return matchesPrefix && matchesSuffix && rowProgressive === progressive;
                }
                return false;
            });
        },

    },

    watch: {
        selectedSupplier(newVal) {
            this.selectedItem = null;
            this.quantity = null;
            
        },
    },

    
};
</script>